import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import config from '../../../content/meta/meta-config';
import brandLogo from '../../images/logo/tfl-brand-logo.png';

// Preview & unfurling https://medium.com/slack-developer-blog/everything-you-ever-wanted-to-know-about-unfurling-but-were-afraid-to-ask-or-how-to-make-your-e64b4bb9254
// Facebook Open Graph Markup https://developers.facebook.com/docs/sharing/webmasters#markup
// Facebook Crawler https://developers.facebook.com/tools/debug/sharing/
// Note: Facebook ID is used for Facebook Analytics.
// Facebook Analytics allows you to understand and optimize your complete customer journey across mobile, web, bots, offline,
// and more. Facebook Analytics is available in a number of Facebook products or you can implement it into your mobile app or website.
// Twitter: support WEBP image format up to maximum of 4096x4096 pixels https://developer.twitter.com/en/docs/twitter-for-websites/cards/overview/summary-card-with-large-image

const getImageFromContentfulPost = (post, imageProp) => {
  const imageSrc = post?.imageCoverHero[imageProp].images?.sources[0]?.srcSet.split(',');
  return imageSrc[imageSrc.length - 1].trim().split(' ')[0];
};

const Seo = (props) => {
  const { data, facebook } = props;
  // console.debug('data', data);
  const isContentfulPost = data?.contentful_id ? true : false;
  const postTitle = data?.frontmatter?.title || data?.title;
  const postDescription = data?.contentful_id ? data?.excerpt?.excerpt : data?.excerpt;
  const postCategory = data?.frontmatter?.category || data?.category;
  const postCover = data?.frontmatter?.cover || data?.imageCoverHero?.file?.imageCoverHeroUrl;
  const postSlug = data?.fields?.slug || data?.slug;
  const datePublished = data?.frontmatter?.datePublished || data?.datePublished;
  const dateModified = data?.frontmatter?.dateModified || data?.updatedAt;
  const postAuthor = data?.frontmatter?.author || data?.author;
  const postTags = data?.frontmatter?.tags || data?.tags;
  const postSeoKeywords =
    !isContentfulPost && data?.frontmatter?.seoKeywords && data?.frontmatter?.seoKeywords.length > 0
      ? data?.frontmatter?.seoKeywords
      : isContentfulPost && data?.seoKeywords
      ? data?.seoKeywords
      : postTags;
  const postSeoVideoEmbedUrl = data?.frontmatter?.seoVideoEmbedUrl;
  const postSeoVideoDuration = data?.frontmatter?.seoVideoDuration;
  const title = postTitle ? `${postTitle} - ${config.shortSiteTitle}` : config.siteTitle;
  const description = postDescription ? postDescription : config.siteDescription;

  let unresizedImage, image, seoImage1x1, seoImage4x3, seoImage16x9, imageFormat;
  if (isContentfulPost) {
    unresizedImage = `https:${postCover}`;
    // image = unresizedImage;
    image = getImageFromContentfulPost(data, 'seoImage');
    seoImage1x1 = getImageFromContentfulPost(data, 'seoImage1x1');
    seoImage4x3 = getImageFromContentfulPost(data, 'seoImage4x3');
    seoImage16x9 = getImageFromContentfulPost(data, 'seoImage16x9');
    imageFormat = 'image/webp';
  } else {
    unresizedImage =
      postCover && postCover.publicURL
        ? config.siteUrl + postCover.publicURL
        : config.siteUrl + config.siteImage;
    image =
      postCover && postCover.childImageSharp.seoImage.src
        ? config.siteUrl + postCover.childImageSharp.seoImage.src
        : config.siteUrl + config.siteImage;
    seoImage1x1 =
      postCover && postCover.childImageSharp.seoImage1x1 && postCover.childImageSharp.seoImage1x1.src
        ? config.siteUrl + postCover.childImageSharp.seoImage1x1.src
        : image;
    seoImage4x3 =
      postCover && postCover.childImageSharp.seoImage4x3 && postCover.childImageSharp.seoImage4x3.src
        ? config.siteUrl + postCover.childImageSharp.seoImage4x3.src
        : image;
    seoImage16x9 =
      postCover && postCover.childImageSharp.seoImage16x9 && postCover.childImageSharp.seoImage16x9.src
        ? config.siteUrl + postCover.childImageSharp.seoImage16x9.src
        : image;
    imageFormat = 'image/jpeg';
  }
  const isArticle = postSlug && postSlug !== '';
  const author = postAuthor ? postAuthor : '櫻桃與魚';
  const url = isArticle
    ? config.siteUrl + config.pathPrefix + postSlug
    : data?.indexUrl
    ? data?.indexUrl
    : config.siteUrl;
  const seoItemList = data?.seoItemList;

  // JSON-LD
  const getSchemaOrgJSONLD = () => {
    let jsonLd = [];
    const copyrightYear = new Date().getFullYear();
    if (url === config.siteUrl) {
      jsonLd.push({
        '@context': 'http://schema.org',
        '@type': 'WebSite',
        url,
        name: title,
        alternateName: config.siteTitle,
      });
    }
    // Logo or the organization on Google Search https://developers.google.com/search/docs/data-types/logo
    jsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      url: 'https://wholovetravel.com/',
      logo: `${config.siteUrl}${brandLogo}`,
    });
    if (postCategory === '美味廚房' && !isContentfulPost) {
      // enable Recipe type from Google Search https://developers.google.com/search/docs/data-types/recipe
      let videoType = null;
      if (postSeoVideoEmbedUrl && postSeoVideoEmbedUrl !== '') {
        videoType = {
          '@type': 'VideoObject',
          name: title,
          description: description,
          thumbnailUrl: [seoImage1x1, seoImage4x3, seoImage16x9],
          contentUrl: postSeoVideoEmbedUrl,
          embedUrl: postSeoVideoEmbedUrl,
          uploadDate: datePublished,
          duration: postSeoVideoDuration,
        };
      }

      // BreadcrumbList
      jsonLd.push({
        '@context': 'http://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '誰都愛旅行',
            item: 'https://wholovetravel.com/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: '美味廚房',
            item: 'https://wholovetravel.com/food/',
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: postTags[0],
            item: `https://wholovetravel.com/tags/${postTags[0]}/`,
          },
          {
            '@type': 'ListItem',
            position: 4,
            name: title,
            item: url,
          },
        ],
      });
    } else {
      // enable Article from Google Search https://developers.google.com/search/docs/data-types/article
      jsonLd.push({
        '@context': 'http://schema.org',
        '@type': 'Article',
        url,
        name: title,
        alternateName: config.siteTitle,
        headline: title,
        image: [seoImage1x1, seoImage4x3, seoImage16x9],
        description,
        keywords: postSeoKeywords,
        author: {
          '@type': 'Person',
          name: author,
          url: 'https://www.facebook.com/wholovetravel',
        },
        copyrightHolder: {
          '@type': 'Person',
          name: author,
        },
        copyrightYear,
        publisher: {
          '@type': 'Organization',
          url: config.siteUrl,
          logo: {
            '@type': 'ImageObject',
            url: `${config.siteUrl}${brandLogo}`,
          },
          name: author,
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': config.siteUrl,
        },
        inLanguage: config.siteLanguage,
        datePublished: datePublished,
        dateModified: dateModified,
      });

      if (postCategory === '旅行日誌') {
        const isTahiti =
          postTags.includes('大溪地') || postTags.includes('波拉波拉島') || postTags.includes('茉莉亞島');
        const isBali = postTags.includes('峇里島自由行');
        const isPhuQuoc = postTags.includes('越南富國島');
        const isDubai = postTags.includes('杜拜自由行');
        const hasDestination = isTahiti || isBali || isPhuQuoc || isDubai;
        const name = isTahiti
          ? '大溪地'
          : isBali
          ? '峇里島'
          : isPhuQuoc
          ? '富國島'
          : isDubai
          ? '杜拜'
          : postTags[0];
        const itemURL = isTahiti
          ? 'travel-tahiti'
          : isBali
          ? 'travel-bali'
          : isPhuQuoc
          ? 'travel-phu-quoc'
          : isDubai
          ? 'travel-dubai'
          : postTags[0];
        let travelPostBreadCrumb = {};
        if (hasDestination) {
          // BreadcrumbList
          travelPostBreadCrumb = {
            '@context': 'http://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '誰都愛旅行',
                item: 'https://wholovetravel.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '旅行日誌',
                item: 'https://wholovetravel.com/travel/',
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: name,
                item: `https://wholovetravel.com/${itemURL}/`,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: title,
                item: url,
              },
            ],
          };
        } else {
          travelPostBreadCrumb = {
            '@context': 'http://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '誰都愛旅行',
                item: 'https://wholovetravel.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '旅行日誌',
                item: 'https://wholovetravel.com/travel/',
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: postTags[0],
                item: `https://wholovetravel.com/tags/${postTags[0]}/`,
              },
              {
                '@type': 'ListItem',
                position: 4,
                name: title,
                item: url,
              },
            ],
          };
        }
        jsonLd.push(travelPostBreadCrumb);
      } else if (url === config.siteUrl) {
        jsonLd.push({
          '@context': 'http://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '誰都愛旅行',
              item: url,
            },
          ],
        });
      } else {
        jsonLd.push({
          '@context': 'http://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '誰都愛旅行',
              item: 'https://wholovetravel.com/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: title,
              item: url,
            },
          ],
        });
      }
    }

    // enable carousel from Google Search https://developers.google.com/search/docs/data-types/carousel
    if (seoItemList && seoItemList.length > 0) {
      jsonLd.push({
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: seoItemList.map((listItem, index) => {
          let rObj = {};
          rObj['@type'] = 'ListItem';
          rObj.position = index + 1;
          rObj.url = listItem;
          return rObj;
        }),
      });
    }
    return jsonLd;
  };

  const schemaOrgJSONLD = getSchemaOrgJSONLD();

  return (
    <Helmet
      htmlAttributes={{
        lang: config.siteLanguage,
        prefix: 'og: http://ogp.me/ns#',
      }}
    >
      {/* General tags */}
      <title>{title}</title>
      <link data-react-helmet="true" rel="alternate" hrefLang={config.siteLanguage} href={url} />
      <meta data-react-helmet="true" name="description" content={description} />
      <meta data-react-helmet="true" name="author" content={author} />
      <meta data-react-helmet="true" name="image" content={unresizedImage} />
      {/* Schema.org tags */}
      <script data-react-helmet="true" type="application/ld+json">
        {JSON.stringify(schemaOrgJSONLD)}
      </script>
      {/* OpenGraph tags */}
      <meta data-react-helmet="true" property="og:url" content={url} />
      <meta data-react-helmet="true" property="og:title" content={title} />
      <meta data-react-helmet="true" property="og:description" content={description} />
      <meta data-react-helmet="true" property="og:image" content={image} />
      <meta data-react-helmet="true" property="og:image:type" content={imageFormat} />
      <meta data-react-helmet="true" property="og:image:alt" content={config.siteTitle} />
      <meta data-react-helmet="true" property="og:site_name" content={config.siteTitle} />
      {isArticle ? (
        <meta data-react-helmet="true" property="og:type" content="article" />
      ) : (
        <meta data-react-helmet="true" property="og:type" content="website" />
      )}
      {isArticle ? <meta data-react-helmet="true" property="og:rich_attachment" content="true" /> : null}
      {isArticle ? <meta data-react-helmet="true" property="og:image:width" content="1200" /> : null}
      {isArticle ? (
        <meta data-react-helmet="true" property="article:published_time" content={datePublished} />
      ) : null}
      {isArticle ? (
        <meta data-react-helmet="true" property="article:modified_time" content={dateModified} />
      ) : null}
      {/* {isArticle ? <meta property="og:image:height" content="630" /> : null} */}
      {/* Provide a URL to the author's Facebook profile or a page with open graph data of type profile */}
      {isArticle ? (
        <meta
          data-react-helmet="true"
          property="article:author"
          content="https://www.facebook.com/wholovetravel"
        />
      ) : null}
      {isArticle ? <meta data-react-helmet="true" property="article:tag" content={postTags} /> : null}
      <meta data-react-helmet="true" property="fb:app_id" content={facebook.appId} />
      {/* Twitter Card tags */}
      <meta
        data-react-helmet="true"
        name="twitter:creator"
        content={config.authorTwitterAccount ? config.authorTwitterAccount : ''}
      />
      <meta data-react-helmet="true" name="twitter:title" content={title} />
      <meta data-react-helmet="true" name="twitter:description" content={description} />
      <meta data-react-helmet="true" name="twitter:image" content={unresizedImage} />
    </Helmet>
  );
};

Seo.propTypes = {
  data: PropTypes.object,
  facebook: PropTypes.object,
};

export default Seo;
